/** @jsx isolateComponent */

import { css } from '@emotion/react';

import { Colors, createUniqueCustomProperties } from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { UNIVERSAL_TOPBAR_HEIGHT } from '../../../constants';

import type {
  ArticleStubType,
  PromoLinksListType,
  UniversalNavLinkType,
} from '../../../constants';
import { MegaMenuFooter } from '../MegaMenuFooter';
import { MegaMenuSubmenu } from '../MegaMenuSubmenu';
import { Promo } from '../Promo';
import StaticProductLeft from './StaticProductLeft';
import StaticProductRight from './StaticProductRight';

const Vars = createUniqueCustomProperties('MegaMenu', [
  'footerWidth',
  'gridTemplateAreas',
  'gridTemplateColumns',
  'leftBackgroundColor',
  'rightBackgroundColor',
]);

type Props = {
  /**
   * An array of links to display at the bottom of the menu
   */
  bottomLinks?: UniversalNavLinkType[];
  id: string;
  /**
   * Whether or not the mega-menu is open
   */
  isMenuOpen: boolean;
  /**
   * Indicates whether the content displayed in the MegaMenu will be static Product content instead of dynamic data
   */
  isStaticProductTab: boolean;
  /**
   * the layout prop determines the underlying
   * CSS Grid and some CSS custom properties
   */
  layout?: string;
  /**
   * Array of links to render within dropdown menu.
   */
  links: UniversalNavLinkType[];
  /**
   * Function called when closing menu on a link from within the MegaMenu
   */
  onCloseMenu?: () => void;
  /**
   * Article Stubs or Links Lists to display in the menu.
   */
  promoArea?: Array<ArticleStubType | PromoLinksListType>;
};

const Styles = labelStyles('MegaMenu', {
  root: (isMenuOpen: boolean) => [
    css`
      ${Vars.footerWidth}: 90%;
      ${Vars.gridTemplateAreas}: 'left-padding left-content right-content right-padding'
      'left-padding footer right-content right-padding';
      ${Vars.gridTemplateColumns}: minmax(20px, 1fr) minmax(613.33px, 780px) minmax(306.66px, 380px) minmax(20px, 1fr);
      ${Vars.leftBackgroundColor}: ${Colors.neutral100};
      ${Vars.rightBackgroundColor}: ${Colors.neutral200};

      &[data-layout='stretch'] {
        ${Vars.footerWidth}: 100%;
        ${Vars.gridTemplateAreas}: 'left-padding left-content right-content right-padding'
        'lower-left footer footer footer';
        ${Vars.gridTemplateColumns}: minmax(20px, 1fr) minmax(613.33px, 810px) minmax(306.66px, 350px) minmax(20px, 1fr);
        ${Vars.leftBackgroundColor}: ${Colors.neutral200};
        ${Vars.rightBackgroundColor}: ${Colors.neutral100};
      }
    `,
    css({
      backgroundColor: `var(${Vars.leftBackgroundColor})`,
      border: `1px solid ${Colors.neutral400}`,
      position: 'absolute',
      top: UNIVERSAL_TOPBAR_HEIGHT,
      left: 0,
      right: 0,
      minHeight: '100%',
      width: '100%',
      display: `${isMenuOpen ? 'block' : 'none'}`,
    }),
  ],
  megaMenuWrapper: css({
    display: 'grid',
    gridTemplateColumns: `var(${Vars.gridTemplateColumns})`,
    gridTemplateRows: '1fr auto',
    gridTemplateAreas: `var(${Vars.gridTemplateAreas})`,
    minHeight: 410,
  }),
  submenu: css({
    gridArea: 'left-content',
  }),
  submenuLinksContainer: (isStaticProductTab: boolean) =>
    css({
      padding: isStaticProductTab ? '30px 0 20px' : '30px 15px 20px 0',
    }),
  promotionsContainer: (isStaticProductTab: boolean) =>
    css({
      background: `var(${Vars.rightBackgroundColor})`,
      padding: isStaticProductTab ? '53px 0 40px 40px' : '40px 0 40px 40px',
      gridArea: 'right-content',
      display: 'flex',
      alignItems: isStaticProductTab ? 'center' : 'normal',
    }),

  footerContainer: css({
    boxShadow: `inset 0 1px ${Colors.neutral300}`,
    gridArea: 'footer',
    width: `var(${Vars.footerWidth})`,
    padding: '20px 2px',
    backgroundColor: Colors.neutral100,
  }),
  lowerLeftPadding: {
    gridArea: 'lower-left',
    backgroundColor: Colors.neutral100,
    boxShadow: `inset 0 1px ${Colors.neutral300}`,
  },
  rightColumn: css({
    gridArea: 'right-padding',
    backgroundColor: `var(${Vars.rightBackgroundColor})`,
  }),
});

/**
 * @private
 * MegaMenu component acts as a dropdown on universal nav links
 */
const MegaMenu = (props: Props) => {
  const { bottomLinks, isStaticProductTab, links, promoArea } = props;

  return (
    <div
      css={Styles.root(props.isMenuOpen)}
      data-layout={props.layout}
      id={props.id}
    >
      <div css={Styles.megaMenuWrapper}>
        <div css={Styles.submenu}>
          <div css={Styles.submenuLinksContainer(isStaticProductTab)}>
            {isStaticProductTab ? (
              <StaticProductLeft onCloseMenu={props.onCloseMenu} />
            ) : (
              <MegaMenuSubmenu links={links} onCloseMenu={props.onCloseMenu} />
            )}
          </div>
        </div>
        <div css={Styles.promotionsContainer(isStaticProductTab)}>
          {isStaticProductTab ? (
            <StaticProductRight onCloseMenu={props.onCloseMenu} />
          ) : (
            <Promo promo={promoArea} onCloseMenu={props.onCloseMenu} />
          )}
        </div>
        <div css={Styles.footerContainer}>
          <MegaMenuFooter links={bottomLinks} onCloseMenu={props.onCloseMenu} />
        </div>
        <div css={Styles.lowerLeftPadding} />
        <div css={Styles.rightColumn} />
      </div>
    </div>
  );
};

export default MegaMenu;
